/**
 * PublicPlaylist — v3.0.135 (Phase 3)
 * Anonymous read-only viewer at /p/:playlistId?code=XXXX.
 * Uses the global PlayerContext so playback controls come from the
 * bottom bar (same as owner-facing pages).
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ListMusic, Music, Lock, Play, Pause, Loader2, KeyRound, AlertTriangle,
  MessageSquare, Send,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { usePlayer } from '../context/PlayerContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicPlaylist() {
  const { playlistId } = useParams();
  const [params, setParams] = useSearchParams();
  const codeFromUrl = params.get('code') || '';

  const [codeInput, setCodeInput] = useState(codeFromUrl);
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(false);
  // v3.0.146 — comments
  const [comments, setComments] = useState([]);
  const [commentName, setCommentName] = useState(() => localStorage.getItem('wim_pl_commenter') || '');
  const [commentText, setCommentText] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  const {
    playQueue, playAtIndex, playing, current, playlistId: liveQueueId,
  } = usePlayer();

  const isLiveQueue = liveQueueId === `public:${playlistId}`;
  const nowPlayingId = isLiveQueue ? current?.song_id : null;

  const fetchPlaylist = useCallback(async (code) => {
    if (!code) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${BACKEND}/api/public/playlists/${playlistId}?code=${encodeURIComponent(code)}`,
        { credentials: 'omit' },
      );
      if (res.status === 429) {
        setLocked(true);
        setError('Too many wrong-code attempts. Try again in an hour.');
        return;
      }
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j?.detail || 'Wrong access code');
        return;
      }
      const data = await res.json();
      setPlaylist(data);
    } catch (e) {
      setError(e.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [playlistId]);

  useEffect(() => {
    if (codeFromUrl) fetchPlaylist(codeFromUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFromUrl]);

  // v3.0.146 — Load comments once playlist is unlocked
  const loadComments = useCallback(async (code) => {
    if (!code) return;
    try {
      const res = await fetch(
        `${BACKEND}/api/public/playlists/${playlistId}/comments?code=${encodeURIComponent(code)}`,
        { credentials: 'omit' },
      );
      if (!res.ok) return;
      const data = await res.json();
      setComments(Array.isArray(data?.comments) ? data.comments : []);
    } catch (_e) { /* silent */ }
  }, [playlistId]);
  useEffect(() => {
    if (playlist && codeFromUrl) loadComments(codeFromUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist, codeFromUrl]);

  const submitComment = async (e) => {
    e.preventDefault();
    const text = commentText.trim();
    if (!text) { toast.error('Say something first'); return; }
    if (text.length > 300) { toast.error('Keep it under 300 characters'); return; }
    setPostingComment(true);
    try {
      const res = await fetch(
        `${BACKEND}/api/public/playlists/${playlistId}/comments`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify({ name: commentName, text, code: codeFromUrl }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.detail || 'Post failed');
        return;
      }
      setComments((prev) => [data, ...prev]);
      setCommentText('');
      if (commentName) localStorage.setItem('wim_pl_commenter', commentName);
      toast.success('Thanks for the reaction!');
    } catch (err) {
      toast.error(err.message || 'Post failed');
    } finally {
      setPostingComment(false);
    }
  };

  const fmtCommentDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const code = (codeInput || '').trim();
    if (!/^\d{4}$/.test(code)) {
      toast.error('Enter the 4-digit code');
      return;
    }
    setParams({ code });
  };

  const startPlayback = (startIndex = 0) => {
    const playable = (playlist?.songs || []).filter((s) => !s.locked && s.audio_url);
    if (!playable.length) {
      toast.error('No playable songs in this playlist');
      return;
    }
    playQueue(playlist.songs, {
      startIndex,
      title: playlist.name || 'Shared playlist',
      playlistId: `public:${playlistId}`,
      autoplay: true,
    });
  };

  // Code entry screen
  if (!playlist) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
              <KeyRound className="text-emerald-400" size={28} />
            </div>
            <h1 className="text-2xl font-bold">Enter the access code</h1>
            <p className="text-sm text-zinc-400 mt-1">
              This playlist is protected by a 4-digit code.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              autoFocus
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="1234"
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              disabled={loading || locked}
              className="bg-black/40 border-white/10 text-white text-2xl font-mono tracking-widest text-center h-14"
              data-testid="public-playlist-code-input"
            />
            <Button
              type="submit"
              disabled={loading || locked || codeInput.length !== 4}
              className="w-full bg-emerald-500 text-black hover:bg-emerald-400"
              data-testid="public-playlist-code-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Unlock playlist
            </Button>
            {error && (
              <div className="text-sm text-rose-400 flex items-center gap-2 justify-center" data-testid="public-playlist-error">
                <AlertTriangle size={14} /> {error}
              </div>
            )}
          </form>

          <div className="mt-8 text-center">
            <Link to="/" className="text-xs text-zinc-500 hover:text-zinc-300">
              Wrapped In Music
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const songs = playlist.songs || [];

  const fmtDuration = (sec) => {
    if (!sec) return '';
    const total = Math.floor(sec);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-black text-white pb-40" data-testid="public-playlist-page">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="mb-8 flex items-start gap-5 flex-wrap">
          {playlist.cover_url ? (
            <img
              src={playlist.cover_url}
              alt=""
              className="w-32 h-32 rounded-lg object-cover shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
              data-testid="public-playlist-cover"
            />
          ) : (
            <div className="w-32 h-32 bg-gradient-to-br from-emerald-500/30 to-purple-500/30 rounded-lg flex items-center justify-center shrink-0">
              <ListMusic size={40} className="text-white/60" />
            </div>
          )}
          <div className="flex-1 min-w-[240px]">
            <div className="text-xs uppercase tracking-wider text-zinc-500">
              Shared playlist
            </div>
            <h1 className="text-3xl font-bold mt-1">{playlist.name}</h1>
            {playlist.description && (
              <p className="text-sm text-zinc-400 mt-2">{playlist.description}</p>
            )}
            <div className="text-xs text-zinc-500 mt-3">
              {songs.length} song{songs.length === 1 ? '' : 's'}
            </div>
            <div className="mt-4">
              <Button
                onClick={() => startPlayback(0)}
                className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-full"
                data-testid="public-playlist-play-btn"
              >
                <Play size={18} className="mr-1" />
                Play
              </Button>
            </div>
          </div>
        </div>

        <div className="border border-white/10 rounded-lg overflow-hidden">
          {songs.map((s, i) => (
            <div
              key={s.song_id}
              className={`flex items-center gap-3 px-3 py-2.5 border-b border-white/5 last:border-b-0 group hover:bg-white/5 cursor-pointer ${nowPlayingId === s.song_id ? 'bg-emerald-500/10' : ''}`}
              onClick={() => {
                if (isLiveQueue) playAtIndex(i);
                else startPlayback(i);
              }}
              data-testid={`public-playlist-song-${i}`}
            >
              <div className="w-6 text-center text-xs text-zinc-500 font-mono">
                {nowPlayingId === s.song_id && playing ? (
                  <div className="flex justify-center gap-0.5">
                    <span className="w-0.5 h-3 bg-emerald-400 animate-pulse" />
                    <span className="w-0.5 h-3 bg-emerald-400 animate-pulse" style={{ animationDelay: '150ms' }} />
                    <span className="w-0.5 h-3 bg-emerald-400 animate-pulse" style={{ animationDelay: '300ms' }} />
                  </div>
                ) : (i + 1)}
              </div>
              {s.image_url ? (
                <img
                  src={s.image_url}
                  alt=""
                  className="w-10 h-10 rounded object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                  <Music size={16} className="text-zinc-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className={`text-sm truncate ${nowPlayingId === s.song_id ? 'text-emerald-300 font-medium' : 'text-white'}`}>
                  {s.title || 'Untitled'}
                </div>
                {s.locked && (
                  <div className="text-[10px] text-amber-400 flex items-center gap-1">
                    <Lock size={10} /> Locked by owner
                  </div>
                )}
              </div>
              {s.duration && (
                <div className="text-xs text-zinc-500 font-mono shrink-0">
                  {fmtDuration(s.duration)}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* v3.0.146 — Comments (public listeners) */}
        <section className="mt-10" data-testid="public-comments-section">
          <div className="flex items-center gap-2 mb-3 text-sm text-white/80">
            <MessageSquare size={16} className="text-emerald-300" />
            <h3 className="font-medium">Leave a reaction</h3>
            <span className="text-xs text-zinc-500 ml-auto">{comments.length} comment{comments.length === 1 ? '' : 's'}</span>
          </div>
          <form
            onSubmit={submitComment}
            className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2 mb-4"
          >
            <input
              value={commentName}
              onChange={(e) => setCommentName(e.target.value.slice(0, 40))}
              placeholder="Your name (optional)"
              data-testid="public-comment-name"
              className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:outline-none placeholder:text-zinc-500"
            />
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value.slice(0, 300))}
              placeholder="Say something nice (or funny) — 300 characters max."
              rows={2}
              data-testid="public-comment-text"
              className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-sm text-white focus:outline-none placeholder:text-zinc-500 resize-none"
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-500">{commentText.length}/300</span>
              <Button
                type="submit"
                disabled={postingComment || !commentText.trim()}
                data-testid="public-comment-submit"
                className="bg-emerald-500 text-black hover:bg-emerald-400 h-8"
              >
                {postingComment ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Send size={12} className="mr-1" />}
                Post
              </Button>
            </div>
          </form>
          {comments.length === 0 ? (
            <div className="text-center py-6 text-xs text-zinc-500">
              Be the first to leave a comment.
            </div>
          ) : (
            <div className="space-y-2">
              {comments.map((c) => (
                <div
                  key={c.comment_id}
                  className="p-3 bg-white/5 border border-white/10 rounded"
                  data-testid={`public-comment-${c.comment_id}`}
                >
                  <div className="flex items-center gap-2 text-xs text-emerald-300 mb-0.5">
                    <span className="font-medium">{c.author_name}</span>
                    <span className="text-zinc-600">·</span>
                    <span className="text-zinc-500 font-mono">{fmtCommentDate(c.created_at)}</span>
                  </div>
                  <div className="text-sm text-white whitespace-pre-wrap break-words">{c.text}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="mt-8 text-center text-xs text-zinc-500">
          Powered by{' '}
          <Link to="/" className="text-emerald-300 hover:underline">Wrapped In Music</Link>
        </div>
      </div>
    </div>
  );
}
